import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import TextInput from '../../../../components/TextInput';
import { dateFormats } from '~/utils';
import { useDateTimeFormat, useLocale } from '~/hooks';

const Container = styled.div`
  position: relative;
`;

const StyledInput = styled(TextInput)`
  && {
    padding-right: 3.5rem;

    &.transparent {
      background: transparent;
      color: transparent;

      &::placeholder {
        color: transparent;
      }
    }
  }
`;

const parseDayTime = (input) => {
  if (!input) return null;
  let str = input.trim().toLowerCase();

  str = str.replace(/\s*([ap]m)$/, '$1');
  str = str.replace(/([ap]m)$/, ' $1');

  const formats = ['H', 'HH', 'Hmm', 'HHmm', 'H:mm', 'HH:mm', 'h a', 'ha', 'h:mm a', 'hh:mm a', 'hmma', 'hmm a'];

  const time = moment(str, formats, true);
  if (!time.isValid()) return null;

  return time.format('HH:mm');
};

const formatTime = (value, formatter) => {
  if (!value) return '';

  const time = moment(value, dateFormats.compactTime);

  return formatter.format(time);
};

const TimeOfDayInput = React.forwardRef(
  ({ value, onKeyDown, onChange, onFocus, onBlur, disabled, materialPlaceholder = 'Time', ...props }, ref) => {
    const locale = useLocale();
    const formatter = useDateTimeFormat({ locale, format: dateFormats.compactTime });

    const defaultDisplay = value && parseDayTime(value) ? formatTime(value, formatter) : '';

    const [tempValue, setTempValue] = useState(undefined);

    const displayValue = tempValue !== undefined ? tempValue : defaultDisplay;

    const handleFocus = (e) => {
      setTempValue(defaultDisplay);
      onFocus?.(e);
    };

    const handleChange = (e) => {
      const input = e.target.value;
      setTempValue(input);
      const normalized = parseDayTime(input);
      onChange(normalized || '');
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') e.currentTarget.blur();
      onKeyDown?.(e);
    };

    const handleBlur = (e) => {
      setTempValue(undefined);
      onBlur?.(e);
    };

    return (
      <Container>
        <StyledInput
          {...props}
          ref={ref}
          type="text"
          value={displayValue}
          materialPlaceholder={materialPlaceholder}
          materialAlwaysVisible
          placeholder={formatTime('00:00', formatter)}
          maxLength={8}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
        />
      </Container>
    );
  },
);

export default TimeOfDayInput;
